import { createContext, useCallback, useContext, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";

const AlertContext = createContext(() => {});

export const useAlert = () => useContext(AlertContext);

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null);
  const [progress, setProgress] = useState(100);

  // Stable across renders -- several components list `showAlert` in a
  // useEffect dependency array, and an unstable reference here (this
  // component re-renders every 40ms while the progress bar animates) was
  // causing those effects to refire in a burst on every toast.
  const showAlert = useCallback((msg, type = "success") => {
    setAlert({ msg, type });
    setProgress(100);
  }, []);

  useEffect(() => {
    if (alert) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            setAlert(null);
            return 0;
          }
          return prev - 2;
        });
      }, 40); // ~2 seconds total
      return () => clearInterval(interval);
    }
  }, [alert]);

  const handleClose = () => {
    setAlert(null);
    setProgress(0);
  };

  return (
    <AlertContext.Provider value={showAlert}>
      {children}
      <div className="fixed top-6 right-6 z-50 space-y-3">
        <AnimatePresence>
          {alert && (
            <motion.div
              initial={{ opacity: 0, x: 150 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 150 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
              className={`relative flex items-start w-80 p-4 rounded-lg shadow-xl text-gray-700 
                ${alert.type === "success" ? "bg-gray-50" : "bg-gray-50"} 
                border border-opacity-20 border-white/20 backdrop-blur-sm`}
            >
              <div className="flex-shrink-0 mr-3">
                {alert.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium leading-5">{alert.msg}</p>
                <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-1 ${alert.type === "success" ? "bg-green-500" : "bg-red-500"}`}
                    initial={{ width: "100%" }}
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: "linear" }}
                  />
                </div>
              </div>
              <button
                onClick={handleClose}
                className="ml-3 flex-shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AlertContext.Provider>
  );
};